/**
 * =============================================================================
 * SPEQ55 RFP Pro - About Page
 * =============================================================================
 *
 * Public about page with company story and mission.
 *
 * @module pages/public/About
 * @author SPEQ55
 */

import { Link } from 'react-router-dom';

/**
 * Mission cards data.
 */
const missionCards = [
  {
    title: 'Democratize Proposal Success',
    description:
      'Make winning proposal strategies accessible to businesses of all sizes, not just large corporations with dedicated bid teams.',
    icon: (
      <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    title: 'Accelerate Response Times',
    description:
      'Reduce proposal preparation time from weeks to days through intelligent automation and AI-powered analysis.',
    icon: (
      <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
  },
  {
    title: 'Increase Win Rates',
    description:
      'Help our clients win more business through better proposals that address every requirement and stand out from the competition.',
    icon: (
      <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
        />
      </svg>
    ),
  },
];

/**
 * Team members data.
 */
const teamValues = [
  {
    title: 'Innovation',
    description: "We constantly push the boundaries of what's possible with AI and automation.",
  },
  {
    title: 'Customer Success',
    description: "Your success is our success. We're committed to helping you win.",
  },
  {
    title: 'Transparency',
    description: 'Clear pricing, honest communication, and no hidden surprises.',
  },
  {
    title: 'Excellence',
    description: 'We strive for excellence in everything we do, from code to customer service.',
  },
];

/**
 * About page component.
 *
 * @returns {JSX.Element} About page
 */
function About() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white">About SPEQ55</h1>
          <p className="mt-6 text-xl text-primary-100 max-w-3xl mx-auto">
            We're helping businesses use AI to better respond to RFPs.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-accent-900 mb-6">Our Story</h2>
              <div className="prose prose-lg text-accent-600">
                <p>
                  Founded in 2025, SPEQ55 was born from a simple observation: responding to RFPs was
                  taking too long and missing too many opportunities.
                </p>
                <p className="mt-4">
                  Our team of industry veterans from engineering and IT came together to solve this
                  problem.
                </p>
                <p className="mt-4">
                  Today, we're proud to help businesses of all sizes transform their RFP response
                  process, saving time and winning more contracts.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm text-center">
                    <p className="text-3xl font-bold text-primary-600">2025</p>
                    <p className="text-sm text-accent-600 mt-1">Founded</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm text-center">
                    <p className="text-xl font-bold text-primary-600">American</p>
                    <p className="text-sm text-accent-600 mt-1">Owned and Engineered</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-accent-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-accent-900">Our Mission</h2>
            <p className="mt-4 text-lg text-accent-600 max-w-2xl mx-auto">
              We're on a mission to level the playing field for businesses competing for contracts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {missionCards.map((card, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow duration-200 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 text-primary-600 mb-6">
                  {card.icon}
                </div>
                <h3 className="text-xl font-semibold text-accent-900 mb-3">{card.title}</h3>
                <p className="text-accent-600">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-accent-900">Our Values</h2>
            <p className="mt-4 text-lg text-accent-600 max-w-2xl mx-auto">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamValues.map((value, index) => (
              <div
                key={index}
                className="border border-accent-200 rounded-lg p-6 hover:border-primary-300 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-accent-900 mb-2">{value.title}</h3>
                <p className="text-sm text-accent-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 transition-colors duration-200"
            >
              View Pricing
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-primary-600 transition-colors duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
